class OrderTracking extends HTMLElement {
  constructor() {
    super();

    // check if url query params contains tracking id or login data
    this.hasTrackingIdInQuery = window.location.search.includes('trackingid');
    this.hasLoginDataInQuery = window.location.search.includes('orderName') && window.location.search.includes('zip');

    // call initial helper functions
    this.selectDomElements();
    this.setInitialScreenVisibility();

    // call display and login screen functions conditionally
    if (this.hasTrackingIdInQuery || this.hasLoginDataInQuery) {
      this.displayTrackingData();
    }
    if (!this.hasTrackingIdInQuery && !this.hasLoginDataInQuery) {
      this.startLoginFlow();
    }
  }

  // helper functions
  selectDomElements() {
    // login screen
    this.loginScreenElem = document.querySelector('.tracking-login-form');
    this.loginTextCenterElem = document.querySelector('.tracking-login-form-text-center');
    this.loginErrorMessageCenterElem = document.querySelector('.tracking-login-form-error-message-center');
    this.loginOrderNumberInputElem = document.querySelector('#tracking-login-form-order-number');
    this.loginPostalCodeInputElem = document.querySelector('#tracking-login-form-postal-code');
    this.loginAllInputElem = document.querySelectorAll('.tracking-login-form-input');
    this.loginButtonElem = document.querySelector('.tracking-login-form-button');
    this.loginErrorMessageBottomElem = document.querySelector('.tracking-login-form-error-message-bottom');
    // details screen
    this.detailsScreenElem = document.querySelector('.tracking-details-container');
    this.detailsCurrentShippingStateImageElem = document.querySelector('.tracking-details-current-shipping-state--image');
    this.detailsCurrentShippingStateTextElem = document.querySelector('.tracking-details-current-shipping-state--text');
    this.detailsLinkShippingProviderElem = document.querySelector('.tracking-details-link-shipping-provider');
    this.detailsOrderNumberElem = document.querySelector('.tracking-details-order-number--number');
    this.detailsEventsContainerElem = document.querySelector('.tracking-details-events-container');
    // error screen
    this.errorScreenElem = document.querySelector('.tracking-error-container');
  }

  setInitialScreenVisibility() {
    this.loginScreenElem.classList.add('hidden');
    this.detailsScreenElem.classList.add('hidden');
    this.errorScreenElem.classList.add('hidden');
  }

  enableButton() {
    this.loginButtonElem.disabled = false;
    this.loginButtonElem.setAttribute("aria-disabled", "false");
  }

  disableButton() {
    this.loginButtonElem.disabled = true;
    this.loginButtonElem.setAttribute("aria-disabled", "true");
  }

  // handle states of login form button
  handleLoginButtonState() {
    this.loginAllInputElem.forEach(elem => {
      elem.addEventListener('input', () => {
        if (this.loginOrderNumberInputElem.value.length != 0 && this.loginPostalCodeInputElem.value.length != 0) {
          this.enableButton();
        } else {
          this.disableButton();
        }
      })
    })
  }

  // get order number & postal code from url
  getLoginDataFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const orderNumber = urlParams.get('orderName');
    const postalCode = urlParams.get('zip');

    return { orderNumber, postalCode };
  }

  // get order number & postal code from login form
  getLoginDataFromForm() {
    const orderNumber = this.loginOrderNumberInputElem.value;
    const postalCode = this.loginPostalCodeInputElem.value;
    return { orderNumber, postalCode };
  }

  startLoginFlow() {
    this.loginScreenElem.classList.remove('hidden');
    this.handleLoginButtonState();
    this.loginButtonElem.addEventListener('click', (event) => {
      event.preventDefault();
      const { orderNumber, postalCode } = this.getLoginDataFromForm();
      if (orderNumber && postalCode) {
        // store login data in url
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set('orderName', orderNumber);
        urlParams.set('zip', postalCode);
        const newUrl = `${window.location.origin}${window.location.pathname}?${urlParams.toString()}`;
        window.history.pushState({}, '', newUrl);
      }
      this.displayTrackingData();
    });
  }

  // get tracking id from url
  getTrackingIdFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const trackingId = urlParams.get('trackingid');
    this.loginScreenElem.classList.add('hidden');
    return trackingId;
  }

  // build api url using tracking id, login data from url or login data from form
  buildApiUrl() {
    const apiUrlPath = 'https://ws-generic-tracker.azurewebsites.net/api/ShipmentData?code=zmn67lOYOdr8aird_bs-_GoHWORybUKfwTSPlC1kQd9fAzFudE8iHA%3D%3D&';
    let params = '';
    if (this.hasTrackingIdInQuery) {
      // use tracking id from url
      params = `shipmentNumber=${this.getTrackingIdFromUrl()}`;
    } else {
      // get login data from url or form
      const loginData = this.hasLoginDataInQuery
        ? this.getLoginDataFromUrl()
        : this.getLoginDataFromForm();
      params = `orderName=${loginData.orderNumber}&zip=${loginData.postalCode}`;
    }
    return `${apiUrlPath}${params}`;
  }

  // create needed html elements for each tracking item
  createTrackingItemHtml() {
    return `<li class="tracking-details-event-item li-st_paw grid--2-col">
              <div class="tracking-details-event-info">
                <p class="tracking-details-event--text"></p>
                <p class="tracking-details-event-date-container">
                  <span class="tracking-details-event--date"></span>
                  <span class="tracking-details-event--separator">|</span>
                  <span class="tracking-details-event--time"></span>
                </p>
              </div>
            </li>`;
  }

  // convert timestamp from api to locale date and time
  convertTimestamp(timestamp) {
    const date = new Date(timestamp);
    // get current shop
    const isUsShop = window.location.host.includes('us.' || 'us-');
    const isIntShop = !(window.location.host.includes('us.' || 'us-'));
    // get location codes
    const localeCode = window.Shopify.locale;
    const countryCode = window.Shopify.country;
    const localizationCode = `${localeCode}-${countryCode}`;
    // for INT Shop de
    if (isIntShop && localeCode === 'de') {
      const eventDate = date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      const eventTime = `${date.toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })} Uhr CEST`;
      return { eventDate, eventTime };
    // for INT Shop en
    } else if (isIntShop && localeCode === 'en') {
      const eventDate = date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
      const eventTime = (`${date.toLocaleTimeString('en-GB', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })} CEST`).toUpperCase();
      return { eventDate, eventTime };
    // for US Shop
    } else if (isUsShop) {
      const eventDate = date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });
      const eventTime = (`${date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })} CEST`).toUpperCase();
      return { eventDate, eventTime };
    // for all other cases
    } else {
      const eventDate = date.toLocaleDateString(localizationCode);
      const eventTime = (`${date.toLocaleTimeString(localizationCode)} CEST`).toUpperCase();
      return { eventDate, eventTime };
    }
  }

  // central error handling
  handleError(errorCode) {
    // if order number and postal code do not match
    if (errorCode === 401 ) {
      this.loginErrorMessageBottomElem.classList.remove('visibility-hidden');
      this.loginTextCenterElem.classList.add('hidden');
      this.loginErrorMessageCenterElem.classList.remove('hidden');
      this.disableButton();
      this.startLoginFlow();
      // all other errors
    } else {
      this.loginScreenElem.classList.add('hidden');
      this.errorScreenElem.classList.remove('hidden');
    }
  }

  // main functions

  // get tracking data from api
  async getTrackingData() {
    try {
      const response = await fetch(this.buildApiUrl());
      // check if order number and postal code are matching for login (api returns 401 if not)
      if (response.status === 401) {
        this.handleError(401);
      }
      if (!response.ok) {
        throw new Error(`${response.status}`)
      }
      return await response.json();
    } catch (error) {
      // handle all other errors
      if (error.message !== '401') {
        this.handleError(Number(error.message) || 500);
      }
    }
  }

  // convert api data to required format
  async convertTrackingData() {
    try {
      const rawTrackingData = await this.getTrackingData();
      if (rawTrackingData) {
        let convertedTrackingData = {
          trackingUrl: rawTrackingData.trackingUrl || '',
          orderNumber: rawTrackingData.shopifyOrderName || '',
          // wsEvent: 1 is not relevant for tracking
          trackingEvents: rawTrackingData.shipmentEvents
            .filter(element => element.wsEvent !== 1)
            .map(({ wsEvent, timestamp }) => {
              // get corresponding shipping text from locales map dynamically
              const eventTextShort = textShippingStates.get?.(`${wsEvent}Short`);
              const eventTextLong = textShippingStates.get?.(`${wsEvent}Long`);
              // get event date and time
              const { eventDate, eventTime } = this.convertTimestamp(timestamp);
              return { eventCode: String(wsEvent), eventTextShort, eventTextLong, eventDate, eventTime }
          })
        };
        // create and add order confirmation event as it doesn't come from the api
        const orderConfirmation = {
          eventCode: 'Oc',
          eventTextShort: textShippingStates.get('OcShort') || '',
          eventTextLong: textShippingStates.get('OcLong') || '',
          eventDate: this.convertTimestamp(rawTrackingData.shopifyOrderDate).eventDate || '',
          eventTime: this.convertTimestamp(rawTrackingData.shopifyOrderDate).eventTime || ''
        };
        convertedTrackingData.trackingEvents.unshift(orderConfirmation);
        return convertedTrackingData;
      }
    } catch (error) {
      console.error(`Conversion Error: ${error.message}`);
      throw error;
    }
  }

  // pass on data to corresponding DOM elements
  async displayTrackingData() {

    try {
      const toBeDisplayedTrackingData = await this.convertTrackingData();
      if (toBeDisplayedTrackingData) {
        this.loginScreenElem.classList.add('hidden');
        this.detailsScreenElem.classList.remove('hidden');
        // get last tracking event
        const lastTrackingEvent = toBeDisplayedTrackingData.trackingEvents[toBeDisplayedTrackingData.trackingEvents.length - 1];
        // display matching image for last event
        const dynamicKey = lastTrackingEvent.eventCode;
        const imageUrl = imageShippingStates.get(dynamicKey);
        if (dynamicKey && imageUrl) {
          this.detailsCurrentShippingStateImageElem.src = imageUrl;
        }
        this.detailsCurrentShippingStateTextElem && (this.detailsCurrentShippingStateTextElem.textContent = lastTrackingEvent.eventTextShort);
        this.detailsLinkShippingProviderElem && (this.detailsLinkShippingProviderElem.href = toBeDisplayedTrackingData.trackingUrl);
        this.detailsOrderNumberElem && (this.detailsOrderNumberElem.textContent = toBeDisplayedTrackingData.orderNumber);
        // remove possibly existing elements to avoid double events
        this.detailsEventsContainerElem.replaceChildren();
        // loop through tracking events array and create needed html elements
        toBeDisplayedTrackingData.trackingEvents.forEach(event => {
          this.detailsEventsContainerElem.insertAdjacentHTML('afterbegin',this.createTrackingItemHtml());
          document.querySelector('.tracking-details-event--text').textContent = event.eventTextLong;
          document.querySelector('.tracking-details-event--date').textContent = event.eventDate;
          document.querySelector('.tracking-details-event--time').textContent = event.eventTime;
        });
      }
    } catch (error) {
      console.error(`Display Error: ${error.message}`);
    }
  }
}

customElements.define('order-tracking', OrderTracking);